import { useState, useEffect, Component } from 'react';
import { supabase, getProfile, signOut } from './lib/supabase';
import LoginScreen from './components/LoginScreen';
import PWABanner from './components/PWABanner';
import KioskView from './components/KioskView';
import QrScreen from './components/QrScreen';
import EmployeeView from './components/EmployeeView';
import AdminView from './components/AdminView';
import PlatformPanel from './components/PlatformPanel';
import PricingPage from './components/PricingPage';
import LegalPage from './components/LegalPage';
import ConsentGate from './components/ConsentGate';
import { getMyConsents } from './lib/org';
import { LEGAL_VERSION } from './legal/documents';
import { getMyOrg, applyOrgBrand, getOrgPublicBrand } from './lib/org';
import { loadMyPolicy, setOrgTimezone } from './lib/policy';
import logoMark from './assets/logo-gestion360.png';

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{background:'#0f172a',minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',padding:'2rem'}}>
          <div style={{background:'#1e293b',borderRadius:'1.5rem',padding:'2rem',maxWidth:'420px',width:'100%'}}>
            <p style={{color:'#f87171',fontWeight:'bold',fontSize:'1rem',marginBottom:'1rem'}}>❌ Error en la aplicación</p>
            <pre style={{color:'#94a3b8',fontSize:'0.7rem',whiteSpace:'pre-wrap',wordBreak:'break-word',marginBottom:'1.5rem',maxHeight:'200px',overflow:'auto'}}>
              {this.state.error?.toString()}
            </pre>
            <button onClick={()=>window.location.reload()}
              style={{background:'var(--brand-grad)',color:'white',border:'none',padding:'0.875rem',borderRadius:'1rem',fontWeight:'bold',cursor:'pointer',width:'100%',fontSize:'0.9rem'}}>
              Recargar
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function Notice({ title, text, onLogout, actionLabel = 'Salir' }) {
  return (
    <div style={{background:'linear-gradient(135deg,#0f172a,#1e293b)',minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',padding:'2rem'}}>
      <div style={{background:'#1e293b',borderRadius:'1.5rem',padding:'2rem',maxWidth:'420px',width:'100%',textAlign:'center'}}>
        <img src={logoMark} alt="Gestión360" style={{width:'3.5rem',height:'3.5rem',borderRadius:'1rem',margin:'0 auto 1rem'}}/>
        <p style={{color:'white',fontWeight:'bold',fontSize:'1.1rem',marginBottom:'.5rem'}}>{title}</p>
        <p style={{color:'#94a3b8',fontSize:'.875rem',marginBottom:'1.5rem'}}>{text}</p>
        <button onClick={onLogout} style={{background:'#2f3d7a',color:'white',border:'none',padding:'.75rem 1.5rem',borderRadius:'1rem',fontWeight:'bold',cursor:'pointer'}}>{actionLabel}</button>
      </div>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [orgInfo, setOrgInfo] = useState(null);
  const [path, setPath] = useState(window.location.pathname);
  const [loading, setLoading] = useState(true);
  const [loginBrand, setLoginBrand] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [recovery, setRecovery] = useState(() => /type=recovery/.test(window.location.hash) || window.location.search.includes('reset=1'));
  const [consents, setConsents] = useState(null); // null = cargando, 'error' = no se pudo leer (no bloquea)

  const loadOrg = async () => {
    try {
      const o = await getMyOrg(); applyOrgBrand(o?.org);
      setOrgTimezone(o?.org?.timezone);
      if (o?.org) await loadMyPolicy().catch(() => {});
      setOrgInfo(o); return o;
    }
    catch (e) { console.error('Error loading organization:', e); setOrgInfo(null); return null; }
  };

  const loadAll = async (user) => {
    try { const p = await getProfile(user.id); setProfile(p); }
    catch (e) { setProfile(null); }
    await loadOrg();
  };

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      if (session?.user) await loadAll(session.user);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) { setSyncing(true); setSession(session); await loadAll(session.user); setSyncing(false); }
      else { setSession(session); setProfile(null); setOrgInfo(null); }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Navegación simple por dirección (sin librerías): /plataforma es el panel del dueño de la plataforma
  const go = (p) => { window.history.pushState({}, '', p); setPath(p); };
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // /o/<slug>: pantalla de ingreso con la marca de esa organización
  useEffect(() => {
    const m = path.match(/^\/o\/([a-z0-9-]+)\/?$/i);
    if (!m) { setLoginBrand(null); return; }
    let alive = true;
    setLoginBrand(undefined); // cargando
    getOrgPublicBrand(m[1]).then((b) => { if (alive) setLoginBrand(b || { missing: true }); }).catch(() => { if (alive) setLoginBrand({ missing: true }); });
    return () => { alive = false; };
  }, [path]);

  // Si se instala la app desde /o/<slug>, el ícono vuelve a abrir esa misma pantalla de ingreso
  useEffect(() => {
    if (!loginBrand?.slug) return;
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return;
    const original = link.getAttribute('href');
    const o = window.location.origin;
    const icons = ['icon-192.png', 'icon-512.png'].map((f) => ({ src: `${o}/${f}`, sizes: f.includes('512') ? '512x512' : '192x192', type: 'image/png', purpose: 'any' }));
    const blob = new Blob([JSON.stringify({ name: loginBrand.name, short_name: String(loginBrand.name).slice(0, 12), start_url: `${o}/o/${loginBrand.slug}`, scope: `${o}/`, display: 'standalone', background_color: '#222d5a', theme_color: '#222d5a', icons })], { type: 'application/manifest+json' });
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    return () => { link.setAttribute('href', original); URL.revokeObjectURL(url); };
  }, [loginBrand]);

  // Enlace de un QR de asistencia (/q/<token>): se guarda y se procesa al entrar a la app
  useEffect(() => {
    const m = path.match(/^\/q\/([A-Za-z0-9._-]+)\/?$/);
    if (!m) return;
    try { sessionStorage.setItem('pendingQr', m[1]); } catch (e) { /* sin storage */ }
    go('/ingresar');
  }, [path]);

  // La dirección principal (/) es la web pública. Si ya hay sesión, cada uno vuelve a su lugar:
  // administración de la plataforma → /plataforma · administradores y empleados → la dirección de su organización
  useEffect(() => {
    if (!session || syncing || (path !== '/' && path !== '/ingresar')) return;
    const dest = orgInfo?.platform_admin ? '/plataforma' : orgInfo?.org?.slug ? `/o/${orgInfo.org.slug}` : null;
    if (dest) { window.history.replaceState({}, '', dest); setPath(dest); }
  }, [session, syncing, orgInfo, path]);

  // Aceptación de Términos / Privacidad (primer ingreso y cada versión nueva)
  useEffect(() => {
    if (!profile?.id) { setConsents(null); return; }
    let alive = true;
    getMyConsents().then((c) => { if (alive) setConsents(c); }).catch(() => { if (alive) setConsents('error'); });
    return () => { alive = false; };
  }, [profile?.id]);

  const handleLogout = async () => {
    const slug = orgInfo?.org?.slug;
    await signOut();
    setSession(null);
    setProfile(null);
    setOrgInfo(null);
    setConsents(null);
    go(orgInfo?.platform_admin ? '/plataforma' : slug ? `/o/${slug}` : '/');
  };

  // Página pública: planes y pedido de prueba (no requiere sesión)
  if (path === '/precios' || (path === '/' && !session && !recovery)) return <PricingPage onLogin={() => go('/ingresar')} />;
  if (path === '/terminos') return <LegalPage kind="terms" onHome={() => go('/')} />;
  if (path === '/privacidad') return <LegalPage kind="privacy" onHome={() => go('/')} />;

  if (loading || syncing) {
    return (
      <div style={{background:'linear-gradient(135deg,#0f172a,#1e293b)',minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}>
        <div style={{textAlign:'center'}}>
          <div style={{width:'3rem',height:'3rem',border:'2px solid #5667a8',borderTopColor:'transparent',borderRadius:'50%',animation:'spin 1s linear infinite',margin:'0 auto 1rem'}}/>
          <p style={{color:'#94a3b8',fontSize:'0.875rem'}}>Cargando...</p>
        </div>
      </div>
    );
  }

  // Enlace de recuperación de contraseña (llega por mail)
  if (recovery) {
    return <LoginScreen forceReset brand={loginBrand} mode={path.startsWith('/plataforma') ? 'platform' : 'org'} onLogin={() => {}} onResetDone={() => { setRecovery(false); go('/'); }} />;
  }

  if (!session) {
    const onLogin = async (p) => { if (p) setProfile(p); await loadOrg(); };
    if (path === '/plataforma' || path.startsWith('/plataforma/')) return <LoginScreen mode="platform" onLogin={onLogin} />;
    if (/^\/o\/[a-z0-9-]+\/?$/i.test(path)) return <LoginScreen mode="org" brand={loginBrand} onLogin={onLogin} />;
    return <LoginScreen mode="portal" onLogin={onLogin} />;
  }

  const isPlatform = !!orgInfo?.platform_admin;
  const orgStatus = orgInfo?.org?.status;
  const orgBlocked = ['suspended', 'cancelled', 'expired'].includes(orgStatus);
  const onPlatformRoute = path === '/plataforma' || path.startsWith('/plataforma/');
  const orgUsable = !!profile && !orgBlocked;

  // Cada organización entra solo por su dirección: una cuenta de otra organización no puede usar /o/<otra>
  const orgRoute = path.match(/^\/o\/([a-z0-9-]+)\/?$/i);
  if (orgRoute && orgRoute[1].toLowerCase() !== (orgInfo?.org?.slug || '')) {
    return <Notice title="Esta cuenta no pertenece a esta organización" text="Cada organización tiene su propia dirección de ingreso. Cerrá sesión e ingresá desde la dirección de tu organización." onLogout={handleLogout} actionLabel="Cerrar sesión" />;
  }

  // Dirección propia de la administración principal: /plataforma
  if (onPlatformRoute) {
    if (!isPlatform) {
      return <Notice title="Sin acceso" text="Esta dirección es solo para la administración de Gestión360. Ingresá desde la dirección de tu organización." onLogout={handleLogout} actionLabel="Cerrar sesión" />;
    }
    return <ErrorBoundary><PlatformPanel path={path} go={go} onBack={orgUsable && orgInfo?.org?.slug ? () => go(`/o/${orgInfo.org.slug}`) : null} onLogout={handleLogout} /></ErrorBoundary>;
  }

  // Administrador de plataforma sin organización propia (o con la suya bloqueada): entra directo al panel
  if (isPlatform && !orgUsable) {
    return <ErrorBoundary><PlatformPanel path={path} go={go} onBack={null} onLogout={handleLogout} /></ErrorBoundary>;
  }

  if (!profile) {
    return <Notice title="Cuenta sin organización" text="Tu usuario todavía no está asociado a ninguna organización. Pedile acceso a quien administra tu cuenta." onLogout={handleLogout} />;
  }

  if (orgStatus === 'expired') {
    return <Notice title="La prueba gratuita terminó" text="El período de prueba de tu organización venció. Comunicate con Gestión360 para activar tu plan; tus datos están guardados." onLogout={handleLogout} />;
  }

  if (['suspended','cancelled'].includes(orgStatus)) {
    return <Notice title="Cuenta suspendida" text="El acceso de tu organización está suspendido. Comunicate con el administrador para reactivarlo." onLogout={handleLogout} />;
  }

  // Cuenta de kiosco: tablet fija, sin avisos de privacidad personales
  if (profile.role === 'kiosk') {
    return <ErrorBoundary><KioskView onLogout={handleLogout} /></ErrorBoundary>;
  }

  const needed = profile.role === 'admin' ? ['terms', 'privacy'] : ['privacy'];
  if (consents === null) {
    return <div style={{background:'linear-gradient(135deg,#0f172a,#1e293b)',minHeight:'100vh'}} />;
  }
  if (Array.isArray(consents) && needed.some((d) => !consents.some((c) => c.doc === d && c.version === LEGAL_VERSION))) {
    return <ConsentGate isAdmin={profile.role === 'admin'} onAccepted={() => getMyConsents().then(setConsents).catch(() => setConsents('error'))} onLogout={handleLogout} />;
  }

  if (profile.role === 'admin' && path === '/pantalla') {
    return <QrScreen onBack={() => go('/')} />;
  }

  if (profile.role === 'admin') {
    return <ErrorBoundary><AdminView profile={profile} onLogout={handleLogout} orgInfo={orgInfo} onOrgChanged={loadOrg} onOpenPlatform={() => go('/plataforma')} /><PWABanner orgName={orgInfo?.org?.name} logoUrl={orgInfo?.org?.logo_url}/></ErrorBoundary>;
  }

  return <ErrorBoundary><EmployeeView profile={profile} orgInfo={orgInfo} onLogout={handleLogout} /><PWABanner orgName={orgInfo?.org?.name} logoUrl={orgInfo?.org?.logo_url}/></ErrorBoundary>;
}
